import React, { useEffect, useState } from 'react';
import { Table, message } from 'antd';
import { getReportApiByKey } from '@app/api/reports.api';

interface DepartmentData {
  id: string | number;
  code: string;
  name: string;
  import_total?: number;
  export_total?: number;
}

interface CategoryData {
  id: string | number;
  code: string;
  name: string;
  import_total?: number;
  export_total?: number;
  departments?: DepartmentData[];
}

interface CountryData {
  id: string | number;
  name: string;
  import_total?: number;
  export_total?: number;
  categories?: CategoryData[];
}

interface ConsignmentByCountryReportProps {
  reportKey: string;
  filters: any;
}

const ConsignmentByCountryReport: React.FC<ConsignmentByCountryReportProps> = ({ reportKey, filters }) => {
  const [data, setData] = useState<CountryData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!reportKey || !filters) return;

      const reportApi = getReportApiByKey(reportKey);
      if (!reportApi) {
        message.error('Invalid report key.');
        return;
      }

      setLoading(true);
      try {
        const response = await reportApi.generateReport(filters);

        // Transform the nested object structure to array
        const countries: CountryData[] = Object.entries(response?.report?.countries || {}).map(
          ([countryId, countryData]: [string, any]) => {
            const categories = Object.entries(countryData.categories || {}).map(
              ([categoryId, categoryData]: [string, any]) => {
                const departments = Object.entries(categoryData.departments || {}).map(
                  ([deptId, deptData]: [string, any]) => ({
                    id: deptId,
                    code: deptData.department_code,
                    name: deptData.department_name,
                    import_total: deptData.import_total || 0,
                    export_total: deptData.export_total || 0,
                  }),
                );

                return {
                  id: categoryId,
                  code: categoryData.product_category_code,
                  name: categoryData.product_category_name,
                  import_total: categoryData.import_total || 0,
                  export_total: categoryData.export_total || 0,
                  departments: departments,
                };
              },
            );

            return {
              id: countryId,
              name: countryData.country_name,
              import_total: countryData.import_total || 0,
              export_total: countryData.export_total || 0,
              categories: categories,
            };
          },
        );

        setData(countries);
      } catch (err) {
        console.error(err);
        message.error('Failed to fetch Consignment by Country report data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [reportKey, filters]);

  const columns: any[] = [
    {
      title: 'Country / Category / Department',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Import Count',
      dataIndex: 'import_total',
      key: 'import_total',
      render: (value: number | undefined) => (value !== undefined ? value.toLocaleString('en-LK') : '-'),
    },
    {
      title: 'Export Count',
      dataIndex: 'export_total',
      key: 'export_total',
      render: (value: number | undefined) => (value !== undefined ? value.toLocaleString('en-LK') : '-'),
    },
  ];

  // Recursive transformation for nested hierarchy (Country → Category → Department)
  const transformData = (countries: CountryData[]) =>
    countries.map((country) => ({
      key: country.id,
      name: country.name,
      import_total: country.import_total,
      export_total: country.export_total,
      children: country.categories?.map((category) => ({
        key: `${country.id}-${category.id}`,
        name: `↳ ${category.code} - ${category.name}`,
        import_total: category.import_total,
        export_total: category.export_total,
        children: category.departments?.map((dept) => ({
          key: `${country.id}-${category.id}-${dept.id}`,
          name: `↳↳ ${dept.code} - ${dept.name}`,
          import_total: dept.import_total,
          export_total: dept.export_total,
        })),
      })),
    }));

  const tableData = transformData(data);

  return (
    <Table
      title={() => <div style={{ textAlign: 'center', fontWeight: 700 }}> Consignments by Country Report</div>}
      columns={columns}
      dataSource={tableData}
      pagination={false}
      bordered
      loading={loading}
      expandable={{
        defaultExpandAllRows: true,
      }}
    />
  );
};

export default ConsignmentByCountryReport;
